/**
 * Stage names from earlier vocabularies, kept only so a link that predates a
 * rename still lands on the column it meant.
 *
 * The Board's `?column=` deep link is not just a label - it is what the
 * Command Center funnel builds, and what a user may have bookmarked or pasted
 * into a chat. Those links outlive the migration that renamed the stage.
 *
 * ── Why every entry points at TODAY, not at the next hop ──────────────────
 *
 * The previous version of this file mapped `Quote → Pricing`, which was
 * correct for exactly five days:
 *
 *     2026-08-13   Quote    → Pricing
 *     2026-08-18   Pricing  → Proposal
 *     2026-08-22   Proposal → Presentation
 *     that map     Quote    → Pricing     ← stopped at the first rename
 *
 * `Pricing` no longer exists, so an old `?column=Quote` was translated into a
 * column the Board does not have, failed the known-column guard, and was
 * dropped in silence - the precise failure this file was written to prevent.
 * A shim written as "the next hop" needs re-deriving on every rename and will
 * eventually be one hop short again; a shim written as "where this ends up
 * today" only ever needs its new keys added.
 *
 * Values are stage CODES, which is also why this file should stop growing:
 * codes do not change when a tenant renames a column, so from here on a
 * rename adds no entries. Only a change of code does.
 *
 * A test asserts every value is a code the product actually ships. Had that
 * test existed, the bug above would not have survived.
 */
export const LEGACY_STAGE_NAMES: Record<string, string> = {
  // Quote → Pricing → Proposal → Presentation
  Quote: 'Presentation',
  Pricing: 'Presentation',
  Proposal: 'Presentation',
  // CommitOnboard → Negotiating → Negotiation
  CommitOnboard: 'Negotiation',
  Negotiating: 'Negotiation',
  // Contacted was deleted rather than renamed
  // (db/changes/2026-08-22--lead-stage-vocabulary.sql): its leads went back to
  // Prospect, which is where a lead being worked but not yet presented to now
  // lives. That is where an old `?column=Contacted` link means, so that is
  // where it lands - the alternative is a column no active stage has, which
  // the known-column guard drops without a word.
  Contacted: 'Prospect',
  // Vacation Hold, Collections and Closed were never sales stages: they were
  // customer account statuses left behind when account_status became
  // lead_stage. db/changes/2026-08-23 removed the untouched ones and
  // 2026-08-29 finished the job by code, moving every lead on them to the
  // tenant's own Prospect. An old `?column=` naming one of them means those
  // leads, so it lands where they went.
  'Vacation Hold': 'Prospect',
  Collections: 'Prospect',
  Closed: 'Prospect',
};

/** The code for a stage string that may have come from an old link. */
export function canonicalStage(stage: string): string {
  return LEGACY_STAGE_NAMES[stage] ?? stage;
}
